import { useEffect, useState } from "react";
import { useAuth } from "../App";
import { getCurrentPrivacyPolicy, acceptPrivacyPolicy } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";

/**
 * Blocks the app with a non-dismissable modal when the super admin has published a
 * newer privacy policy than the current user has accepted. Regular staff must accept
 * before continuing. Testers (read-only) and super admins are exempt.
 */
export const PrivacyGate = () => {
  const { user, refreshUser, isTester } = useAuth();
  const [policy, setPolicy] = useState(null);
  const [accepting, setAccepting] = useState(false);

  const currentVersion = user?.privacy_policy_version || 0;
  const acceptedVersion = user?.privacy_policy_accepted_version || 0;
  const needsAcceptance =
    !!user && !user.is_super_admin && !isTester() && currentVersion > 0 && acceptedVersion < currentVersion;

  useEffect(() => {
    if (needsAcceptance) {
      getCurrentPrivacyPolicy().then((res) => setPolicy(res.data)).catch(() => {});
    }
  }, [needsAcceptance]);

  if (!needsAcceptance) return null;

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await acceptPrivacyPolicy();
      await refreshUser();
      toast.success("Thank you — privacy policy accepted");
    } catch {
      toast.error("Could not record acceptance. Please try again.");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      data-testid="privacy-gate"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 border-b bg-[#00305E] text-white rounded-t-xl">
          <h2 className="text-lg font-bold">Updated Privacy Policy</h2>
          <p className="text-xs text-white/80 mt-1">
            Please review and accept our updated Privacy Policy to continue using SMARTIS.
          </p>
        </div>
        <div
          className="px-6 py-4 overflow-y-auto text-sm text-slate-700 whitespace-pre-wrap leading-relaxed flex-1"
          data-testid="privacy-gate-content"
        >
          {policy ? policy.content : "Loading policy…"}
        </div>
        <div className="px-6 py-4 border-t flex items-center justify-end gap-3">
          <Button
            onClick={handleAccept}
            disabled={accepting || !policy}
            className="bg-[#00305E] hover:bg-[#00305E]/90"
            data-testid="privacy-gate-accept-btn"
          >
            {accepting ? "Saving…" : "I Accept"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyGate;
